import {
  BaseProductType,
  Coordinate,
  GridProduct,
  IconicColor,
  IconicColorDisplayName,
  IconicColorImportName,
  InterfaceProduct,
  InterfaceProductCategory,
  MechProduct,
  ProductOrientation,
  ProductOrientationSupport,
  SkinProduct,
  SkinProductOption,
  SkinProductOptionWithGrid,
  TopLevelMenuCategory,
} from '../types/productTypes'
import { throwErrorInline } from '../utils/throwErrorInline'
import {
  ConfiguredInterface,
  ConfiguredProductListItem,
  InflatedConfiguredInterfaceProduct,
  InflatedListItemProduct,
} from '../list/ListTypes'
import { ProductDataShape } from '../ProductDataShape'
import { findOptimalGrid, getAllGridOptions } from './findOptimalGrid'
import { SelectorScreenMenuType } from '../selector-screen/SelectorScreenMenuType'
import { logErrorDebug } from '../debug/debugLogging'
import { SocketMenus, WiserMenus } from '../selector-screen/SelectorScreenMenuType'

export function getAllowedInterfaceCategories(productType: BaseProductType, isPro: boolean): InterfaceProductCategory[] {
  switch (productType) {
    case 'Switch':
      return ['Switches', 'Dimmers', 'Rotary Switches', 'USB', 'Bluetooth', 'LED', 'Network', 'Accessories', 'Pro Placeholder']
    case 'Socket':
      return ['Switches', 'USB', 'Pro Placeholder']
    case 'Other':
      return []
  }
}

export function isProductConfigurable(product: SkinProductOption) {
  return product.gridProduct
    && doesSkinHaveSlots(product)
}

export function getDefaultSkinOrientation(product: SkinProductOption): ProductOrientation {
  return getDefaultOrientation(product.gridProduct ?? null)
}

export function getDefaultOrientation(product: GridProduct | null): ProductOrientation {
  if (product?.orientation === 'Horizontal') {
    return 'Horizontal'
  }

  return 'Vertical'
}

export function getSkinSlotCount(skinProduct: SkinProductOption): number {
  // skin may hide the slots on the grid
  if (skinProduct.disableSlots) {
    return 0
  }

  return skinProduct.gridProduct?.slotCount ?? 0
}

export function doesSkinHaveSlots(skinProduct: SkinProductOption): boolean {
  return getSkinSlotCount(skinProduct) > 0
}

export function getSkinSlotPositions(skinProduct: SkinProductOption): readonly Coordinate[] {
  // skin may hide the slots on the grid
  if (!doesSkinHaveSlots(skinProduct)) {
    return []
  }

  return skinProduct.gridProduct?.slotPositions ?? []
}

const ColorMap: { [key in IconicColor]: IconicColorDisplayName } = {
  'SV': 'Silver',
  'SH': 'Silver Shadow',
  'VW': 'Vivid White',
  'XW': 'Xtra White', // Previously 'Vivid White Solid Edge'
  'XB': 'Xtra Black', // Previously 'Black'
  'WY': 'Warm Grey', // discontinued
  'CY': 'Cool Grey',
  'AN': 'Anthracite',
  'CE': 'Crowne',
  'AW': 'Arctic White',
  'AG': 'Ash Grey',
  'ProXW': 'Xtra White', // Previously 'White'
  'ProBK': 'Black',
  'ProRD': 'Red',
  'ProGR': 'Green',
  'ProDB': 'Blue',
  'OutdoorXW': 'Xtra White',
  'OutdoorBK': 'Xtra Black',
} as const

const ColorImportMap: { [key in IconicColor]: IconicColorImportName } = {
  'VW': 'Iconic Vivid White',
  'XW': 'Iconic Vivid White Solid Edge',
  'XB': 'Iconic Black',
  'WY': 'Iconic Warm Grey', // discontinued
  'CY': 'Iconic Cool Grey',
  'AN': 'Iconic Anthracite',
  'SV': 'Styl Silver',
  'SH': 'Styl Silver Shadow',
  'CE': 'Styl Crowne',
  'AW': 'Essence Arctic White',
  'AG': 'Essence Ash Grey',
  'ProXW': 'Pro White',
  'ProBK': 'Pro Black',
  'ProRD': 'Pro Red',
  'ProGR': 'Pro Green',
  'ProDB': 'Pro Blue',
  'OutdoorXW': 'Outdoor White',
  'OutdoorBK': 'Outdoor Black'
} as const

// as per defined colour combination rules
const AdditionalColorCompatibilityMap: { [key in IconicColor]?: IconicColor } = {
  'AW': 'VW',
  'XW': 'VW',
  'CE': 'WY',
  'SH': 'AN',
  'SV': 'CY',
  'ProXW': 'VW',
  'XB' : 'ProBK', // iconic Extra Black skins are compatible with interfaces in colour ProBK
  'OutdoorXW': 'VW',
} as const

export function isColorDiscontinued(color: IconicColor | null): boolean {
  if (color === null) {
    return false
  }

  // const discontinuedColors = ['WY']
  // return discontinuedColors.includes(color)

  return false
}

export function isProCompatibilityAllowed(interfaceProduct: InterfaceProduct, isPro: boolean): boolean {
  if (!isPro && interfaceProduct.pro) {
    return false
  }

  const isInterfaceProCompatible = !!(interfaceProduct.proCompatible || interfaceProduct.pro)

  if (isPro && !isInterfaceProCompatible) {
    return false
  }

  return true
}

export function getCompatibleInterfaces(skinColor: IconicColor, isPro: boolean, interfaces: readonly InterfaceProduct[]) {
  const additional = AdditionalColorCompatibilityMap[skinColor] ?? null
  const compatibleColors: readonly IconicColor[] = [skinColor, ...(additional ? [additional] : [])]

  const exactMatchBaseItemNos: { [baseItemNo: string]: string } = { }

  return interfaces
    .filter(int => {
      if (!isProCompatibilityAllowed(int, isPro)) {
        return false
      }

      const color = int.color

      // only show the individual components
      if (Array.isArray(int.includedComponents)) {
        return false
      }

      if (color == null) {
        return true
      }

      if (color === skinColor && int.baseItemNo) {
        exactMatchBaseItemNos[int.baseItemNo] = int.itemNo
      }

      return compatibleColors.includes(color)
    })
    .filter(int => {
      // remove if an exact color much exists for same baseItemNo
      if (int.baseItemNo) {
        const exactMatchBaseItemNo = exactMatchBaseItemNos[int.baseItemNo]
        if (exactMatchBaseItemNo && exactMatchBaseItemNo !== int.itemNo) {
          return false
        }
      }

      return true
    })
}

export function getAdditionalSkinGridOptions(skin: SkinProductOption, skins: readonly SkinProductOption[]): readonly SkinProductOptionWithGrid[] {
  const baseItemNo = skin.gridProduct?.baseItemNo
  if (baseItemNo) {
    return skins
      .filter((otherSkin): otherSkin is SkinProductOptionWithGrid => {
        return otherSkin.gridProduct !== null
          && baseItemNo === otherSkin.gridProduct?.baseItemNo
          && skin.type === otherSkin.type // handle night walk, etc
          && skin.color === otherSkin.color
      })
  }

  return []
}

export function getOptionalColorDisplayName(color: IconicColor | undefined | null): string | null {
  if (typeof color === 'string') {
    return ColorMap[color]
  }

  return null
}

export function getColorDisplayName(color: IconicColor | null): string {
  if (color === null) {
    return ''
  }

  return ColorMap[color]
}

export function getColorCodeFromImportName(colorName: IconicColorImportName): IconicColor {
  const match = Object.keys(ColorMap).find(key => ColorImportMap[key as IconicColor] === colorName) as IconicColor | undefined
  if (!match) {
    throw Error(`color not found: ${colorName}`)
  }

  if (isColorDiscontinued(match)) {
    throw Error(`Color has been discontinued: ${colorName}`)
  }

  return match
}


export function tryFindInterfaceInColor(interfaceProduct: InterfaceProduct, color: IconicColor, interfaces: readonly InterfaceProduct[]): InterfaceProduct | null {
  const { baseItemNo } = interfaceProduct

  if (!baseItemNo) {
    return interfaceProduct
  }

  const exactMatch = interfaces.find(it => it.baseItemNo === baseItemNo && it.color === color)
  if (!!exactMatch) {
    return exactMatch
  }

  const alternateColor = interfaceProduct.color ? AdditionalColorCompatibilityMap[color] : null
  const allowedMatch = alternateColor ? interfaces.find(it => it.baseItemNo === baseItemNo && it.color === alternateColor) : null

  return allowedMatch ?? null
}

/**
 *
 * @param product returns the correct number of interfaces for the product, fills in gaps with the default option
 * @param selectedButtons
 * @param Products
 * @param replace
 */
export function resolveInterfaces(product: SkinProductOption, selectedButtons: readonly ConfiguredInterface[], Products: ProductDataShape, replace?: boolean): ConfiguredInterface[] {
  const { interfaces, interfaceMap } = Products

  const slotCount = getSkinSlotCount(product)
  const includedInterface = product.includedRockerItemNo

  if (!slotCount) {
    return []
  }

  return Array<string | null>(slotCount)
    .fill(includedInterface)
    .map((defaultInterface, i) => {
      const configured = selectedButtons[i] as ConfiguredInterface | undefined

      let interfaceProduct: InterfaceProduct

      // skin should always have an included rocker item if it has configurable buttons
      const defaultInterfaceProduct = findInterfaceByItemNoInMap(defaultInterface ?? throwErrorInline(`missing default interface for ${product.itemNo}`), interfaceMap)

      if (!!configured) {
        interfaceProduct = findInterfaceByItemNoInMap(configured.interfaceItemNo, interfaceMap)

        if (!isProCompatibilityAllowed(interfaceProduct, product.pro === true)) {
          interfaceProduct = defaultInterfaceProduct
        }
      } else {
        interfaceProduct = defaultInterfaceProduct
      }

      const exactMatch = product.color ? tryFindInterfaceInColor(interfaceProduct, product.color, interfaces) : null

      interfaceProduct = exactMatch ?? (replace ? defaultInterfaceProduct : interfaceProduct)
      const mechItemNo = interfaceProduct.mechs.find(it => it === configured?.mechItemNo) ?? interfaceProduct.mechs[0] ?? null

      return ({
        interfaceItemNo: interfaceProduct.itemNo,
        mechItemNo: mechItemNo ?? null,
      })
    })
}

export function findSkinByItemNo<T extends SkinProduct>(itemNo: string, skins: readonly T[]): T {
  return skins.find(it => it.itemNo === itemNo)
    ?? throwErrorInline(`skin ${itemNo} not found`)
}

// null itemNo guaranteed to return null
export function findGridByItemNo(itemNo: null, grids: readonly GridProduct[]): null
// non-null itemNo always returns a product, or throws
export function findGridByItemNo(itemNo: string, grids: readonly GridProduct[]): GridProduct
export function findGridByItemNo(itemNo: null | string, grids: readonly GridProduct[]): GridProduct | null

export function findGridByItemNo(itemNo: string | null, grids: readonly GridProduct[]): GridProduct | null {
  return itemNo === null ? null : grids.find(it => it.itemNo === itemNo)
    ?? throwErrorInline(`grid ${itemNo} not found`)
}

export function findInterfaceByItemNo(itemNo: string, interfaces: readonly InterfaceProduct[]): InterfaceProduct {
  return interfaces.find(it => it.itemNo === itemNo)
  ?? throwErrorInline(`interface ${itemNo} not found`)
}

export function findInterfaceByItemNoInMap(itemNo: string, interfaces: Record<string, InterfaceProduct>): InterfaceProduct {
  return interfaces[itemNo]
    ?? throwErrorInline(`interface ${itemNo} not found`)
}

export function findMechByItemNo(itemNo: string, mechs: readonly MechProduct[]): MechProduct
export function findMechByItemNo(itemNo: null, mechs: readonly MechProduct[]): null
export function findMechByItemNo(itemNo: string | null, mechs: readonly MechProduct[]): MechProduct | null

export function findMechByItemNo(itemNo: string | null, mechs: readonly MechProduct[]): MechProduct | null {
  return itemNo === null ? null : mechs.find(it => it.itemNo === itemNo)
    ?? throwErrorInline(`mech ${itemNo} not found`)
}

export function findMechByItemNoInMap(itemNo: string, mechs: Record<string, MechProduct>): MechProduct
export function findMechByItemNoInMap(itemNo: null, mechs: Record<string, MechProduct>): null
export function findMechByItemNoInMap(itemNo: string | null, mechs: Record<string, MechProduct>): MechProduct | null

export function findMechByItemNoInMap(itemNo: string | null, mechs: Record<string, MechProduct>): MechProduct | null {
  if (itemNo === null) {
    return null
  }

  return mechs[itemNo]
    ?? throwErrorInline(`mech ${itemNo} not found`)
}

/**
 * Constructs a fully detailed list of components required for a finished product
 * Based on the mech selection, the grid may be changed from a composite product to equivalent individual components, presenting the (in general) best value combination of components
 * @param listProducts
 * @param products
 */
export function inflateListItems(listProducts: readonly ConfiguredProductListItem[], products: ProductDataShape): readonly InflatedListItemProduct[] {
  return listProducts.map(({ quantity, configuredProduct, note, roomId }) => {
    let skinProduct: Readonly<SkinProductOption> = products.skinGridProducts.find(it => it.itemNo === configuredProduct.skinItemNo && it.orientation === configuredProduct.orientation) ?? throwErrorInline()

    const configuredButtons = configuredProduct.interfaces.map(int => {
      const interfaceProduct = findInterfaceByItemNo(int.interfaceItemNo, products.interfaces)
      const mechProduct = findMechByItemNo(int.mechItemNo, products.mechs)
      const parentInterfaceProduct = findParentInterface(interfaceProduct, products.interfaces)

      return {
        interfaceProduct,
        mechProduct,
        parentInterfaceProduct,
      }
    })

    // Special case:
    //   if a standalone grid item no. is specified on a composite product, and at least 1 mech has been customised (or removed altogether),
    //   the grid is swapped out for the standalone grid
    // This means the BoM will show separate Grid/Skin/Mechs, instead of a composite product
    // Composite products are better value until they are customised, so this should (in general) give the best value buying option
    const { gridProduct } = skinProduct

    if (gridProduct) {
      const { standaloneGridItemNo } = gridProduct

      if (standaloneGridItemNo) {

        const gridOptions = getAllGridOptions(standaloneGridItemNo, products.grids)

        const optimalGrid = findOptimalGrid(skinProduct, configuredProduct.orientation, configuredButtons.map(it => it.mechProduct?.itemNo ?? null), gridOptions)

        // default is used otherwise
        if (optimalGrid !== null) {
          skinProduct = {
            ...skinProduct,
            grid: optimalGrid.itemNo,
            gridProduct: optimalGrid
          }
        }
      }
    }

    return {
      skinProduct,
      configuredButtons,
      ledEnabled: configuredProduct.ledEnabled,
      uid: configuredProduct.uid,
      quantity,
      note,
      roomId,
    }
  })
}

export function getInterfaceDisplayName(interfaceProduct: InterfaceProduct, mechProduct: MechProduct | null): string {
  return mechProduct?.interfaceDisplayNameOverride || interfaceProduct.name
}

/**
 * Tries to determine the product category when a product overrides menuCategories, based on the current menu
 * Limitations:
 *  - only Wiser and Power Points are supported with multiple categories
 *  - Showing a product with multiple categories on the top menu is not supported
 * @returns the matched category, or null if one is not found
 * @param product
 * @param expandedMenu
 */
function getDynamicTopLevelMenuCategoryForProduct(product: SkinProductOption, expandedMenu: SelectorScreenMenuType): TopLevelMenuCategory | null {
  if (!Array.isArray(product.menuCategories)) {
    return null
  }

  if (product.menuCategories.length === 1) {
    return product.menuCategories[0]
  }

  // Don't handle any special behaviour for multiple categories when on the top menu
  if (expandedMenu === SelectorScreenMenuType.TopLevel) {
    return null
  }

  if (
    WiserMenus.has(expandedMenu)
      && product.menuCategories.includes('Wiser')
  ) {
    return 'Wiser'
  }

  if (
    SocketMenus.has(expandedMenu)
      && product.menuCategories.includes('Socket')
  ) {
    return 'Socket'
  }

  logErrorDebug('getTopLevelMenuCategoryForProduct',
    `skin[${product.itemNo}] has multiple menuCategories (${product.menuCategories.join(', ')}), but didn't match a rule based on the expandedMenu "${expandedMenu}"`
  )

  return null
}

/**
 * Given expandedMenu is expanded, determined the current product's top-level category.
 * This handles when a product has multiple possible top-level categories.
 * @param product
 * @param expandedMenu
 */
export function getTopLevelMenuCategoryForProduct(product: SkinProductOption, expandedMenu: SelectorScreenMenuType): TopLevelMenuCategory {
  const dynamicMenu = getDynamicTopLevelMenuCategoryForProduct(product, expandedMenu)

  if (dynamicMenu !== null) {
    return dynamicMenu
  }

  if (product.style === 'Outdoor') {
    return product.style
  }

  return product.type
}

export function inflateConfiguredInterface(option: ConfiguredInterface, products: ProductDataShape): InflatedConfiguredInterfaceProduct {
  const interfaceProduct = findInterfaceByItemNoInMap(option.interfaceItemNo, products.interfaceMap)

  return {
    interfaceProduct,
    parentInterfaceProduct: findParentInterface(interfaceProduct, products.interfaces),
    mechProduct: findMechByItemNoInMap(option.mechItemNo, products.mechMap),
  }
}

export function getDefaultButtons(product: SkinProductOption, Products: ProductDataShape): ConfiguredInterface[] {
  const { gridProduct } = product

  if (!gridProduct || !doesSkinHaveSlots(product)) {
    return []
  }

  const interfaceItemNo = product.includedRockerItemNo ?? throwErrorInline(`no included rocker with skin ${product.itemNo}`)

  // Check if product is a composite product with included mechs.
  if (product.gridProduct?.includedMechItemNo) {
    const mechItemNo: string = product.gridProduct.includedMechItemNo
    return getSkinSlotPositions(product).map((_, i) => ({
      interfaceItemNo,
      mechItemNo,
    }))
  }

  // Otherwise assume first mech is default for an interface product.
  const interfaceProduct = findInterfaceByItemNo(interfaceItemNo, Products.interfaces)
  return getSkinSlotPositions(product).map((_, i) => ({
    interfaceItemNo,
    mechItemNo: interfaceProduct.mechs[0] ?? null,
  }))
}

export function findParentInterface(interfaceProduct: InterfaceProduct, interfaces: readonly InterfaceProduct[]): InterfaceProduct | null {
  return interfaces.find(it => it.includedComponents?.includes(interfaceProduct.itemNo) === true) ?? null
}

export function isLedSupported(interfaceProduct: InterfaceProduct, mechProduct: null): false
export function isLedSupported(interfaceProduct: InterfaceProduct, mechProduct: MechProduct | null): boolean
export function isLedSupported(interfaceProduct: InterfaceProduct, mechProduct: MechProduct | null): boolean {
  if (mechProduct === null) {
    return false
  }

  return mechProduct.supportsLed && interfaceProduct.disableLedSupport !== true
}

export function isSkinOrientationSupported(skin: SkinProduct, targetOrientation: ProductOrientation) {
  const orientationSupport: ProductOrientationSupport = skin.orientationSupport ?? skin.imageOrientation

  if (orientationSupport === 'Both') {
    return true
  }

  return orientationSupport === targetOrientation
}